import React from 'react';
import styled from '@emotion/styled';
import { Text, H1, SmallText } from 'ds/components/typography';
import { Button } from 'ds/components/button';
import { useLocale } from 'locale';
import Modal from 'ds/components/modal';
import { MutationType, AddUGCVariables } from 'store/sagas/apiService/types';
import { inlineMutate } from 'store/state/mutationsResponse/actions';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { RootAction } from 'store/state';
import { Topic } from 'components/user-generated-content/utils/initialUGCConfig';

const CardContainer = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.neutrals.grey11,
  borderRadius: theme.borderRadius.large,
  padding: theme.spacing(4, 3),
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
  whiteSpace: 'normal',
}));

const StyledHeader = styled(Text)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

const StyledDescription = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey2,
  padding: theme.spacing(0, 2),
  marginBottom: theme.spacing(1),
  maxWidth: '400px',
}));

const ContentWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(4),
  width: '100%',
  maxWidth: '338px',
  marginTop: theme.spacing(2),
}));

const MessageGroup = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

const StyledTitle = styled(H1)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

const StyledSubtitle = styled(Text)(({ theme }) => ({
  color: theme.colors.neutrals.grey2,
}));

const Footer = styled.div(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  gap: theme.spacing(3),
}));

interface CreditPromotionCardProps {
  agentId: string;
  onSubmit: (data: AddUGCVariables) => void;
}

const CreditPromotionCard: React.FC<CreditPromotionCardProps> = (props) => {
  const { t } = useLocale();
  const [ isModalOpen, setIsModalOpen ] = React.useState(false);
  return (
    <>
      <CardContainer>
        <StyledHeader weight="bold">
          {t('widget.prices.promotionCard.title')}
        </StyledHeader>

        <StyledDescription>
          {t('widget.prices.promotionCard.description')}
        </StyledDescription>

        <Button
          mode="primary"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            props.onSubmit({
              docId: props.agentId,
              numericUserInput: [],
              textUserInput: [
                {
                  topic: Topic.UpgradeRequest,
                  textReview: props.agentId,
                },
              ],
            });
            setIsModalOpen(true);
          }}
        >
          {t('widget.prices.promotionCard.submit')}
        </Button>
      </CardContainer>
      {isModalOpen ? (
        <Modal
          isOpen
          desktopModalPositionCenter
          onClose={() => setIsModalOpen(false)}
        >
          <ContentWrapper>
            <MessageGroup>
              <StyledTitle weight="bold">{t('widget.prices.promotionCard.modal.title')}</StyledTitle>
              <StyledSubtitle>{t('widget.prices.promotionCard.modal.description')}</StyledSubtitle>
            </MessageGroup>

            <Footer>
              <Button
                mode="primary"
                size="extraLarge"
                fullWidth
              >
                {t('widget.prices.promotionCard.modal.submit')}
              </Button>
            </Footer>
          </ContentWrapper>
        </Modal>
      ) : null}
    </>
  );
};


const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onSubmit: (data: AddUGCVariables) => dispatch(inlineMutate(MutationType.AddUGC, data)),
});

export default connect(null, mapDispatchToProps)(CreditPromotionCard);
